import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

export type BnBadgeVariant = "neutral" | "info" | "success" | "warning" | "error";

/**
 * Maps a build lifecycle status to a badge variant. Exported so pages can label
 * consistently: `<bn-badge .variant=${buildStatusVariant(b.status)}>`.
 */
export function buildStatusVariant(status: string): BnBadgeVariant {
  switch (status) {
    case "succeeded":
      return "success";
    case "failed":
      return "error";
    case "running":
    case "pending":
    case "queued":
      return "info";
    // Interrupted by a server restart: not the build's fault, but it produced
    // nothing — warn rather than error.
    case "stale":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * bn-badge is a small status pill (the build-state / role indicator). It slots
 * its label and colors it by `variant`. Pills are the 999px-radius, soft-bg,
 * strong-text style from bino-cli's toolbar.
 *
 *   <bn-badge variant="success">success</bn-badge>
 *   <bn-badge .variant=${buildStatusVariant(build.status)}>${build.status}</bn-badge>
 */
@customElement("bn-badge")
export class BnBadge extends LitElement {
  @property() variant: BnBadgeVariant = "neutral";
  /** When true, a small leading dot is shown (useful for live/running states). */
  @property({ type: Boolean }) dot = false;

  static override styles = css`
    :host {
      display: inline-flex;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: 0.125rem 0.5rem;
      border-radius: var(--bino-radius-pill);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      line-height: 1.4;
      white-space: nowrap;
    }
    .dot {
      width: 0.4375rem;
      height: 0.4375rem;
      border-radius: 50%;
      background: currentColor;
    }
    .neutral {
      background: var(--bino-neutral-bg);
      color: var(--bino-neutral);
    }
    .info {
      background: var(--bino-info-bg);
      color: var(--bino-info);
    }
    .success {
      background: var(--bino-success-bg);
      color: var(--bino-success);
    }
    .warning {
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .error {
      background: var(--bino-error-bg);
      color: var(--bino-error);
    }
  `;

  override render() {
    return html`<span class="pill ${this.variant}"
      >${this.dot ? html`<span class="dot" aria-hidden="true"></span>` : ""}<slot></slot
    ></span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-badge": BnBadge;
  }
}
